/* =========================================================================
   LEARN HUB — GA4 FUNNEL ANALYTICS
   File: assets/learn-hub-analytics.js   ·   loaded by sections/learn-subnav.liquid

   Measures the pre-purchase funnel so you can see WHICH tool drives sales:
        tool used  →  lock seen  →  CTA clicked  →  (Shopify) purchased
   Purchase itself is already tracked by Shopify/GA4 ecommerce; this fills the
   gap before checkout.

   Three GA4 custom events, all routed through one helper (lhTrack):
     1. learn_hub_tool_used    — first genuine interaction with a tool
     2. learn_hub_lock_viewed  — a locked premium panel scrolls into view
     3. learn_hub_cta_clicked  — a CTA inside a locked panel is clicked

   FAILS SILENTLY. If gtag is absent (consent declined, not loaded, blocked)
   nothing fires and nothing throws — a tool must never break because of
   analytics. Per project rule, all JS lives here in assets/, not in .liquid.

   user_tier is read from a server-rendered data-lh-tier attribute (added in
   sections/learn-subnav.liquid) so it always matches the real gate.
   ========================================================================= */
(function () {
  'use strict';

  /* ---- the single doorway every event leaves through ------------------ */
  function lhTrack(eventName, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
    } catch (e) { /* analytics must never break a tool */ }
  }
  window.lhTrack = lhTrack; // exposed so a tool can fire a precise event if ever wanted

  /* ---- which tool is this page? (one tool per Learn page) ------------- */
  var TOOL_BY_PATH = {
    '/pages/cake-er': 'cake_er',
    '/pages/structure': 'structure',
    '/pages/batch-math': 'batch_math',
    '/pages/pricing': 'pricing',
    '/pages/planner': 'timeline',         /* renamed 2026-08-17; slug kept
                                             'timeline' for GA4 continuity */
    '/pages/learn-timeline': 'timeline',  /* pre-rename path, kept */
    '/pages/serving-size-calculator': 'serving',
    '/pages/bloom-atlas': 'bloom_atlas',
    '/pages/pantry': 'pantry'
  };
  function currentTool() {
    var p = window.location.pathname.replace(/\/+$/, '');
    return TOOL_BY_PATH[p] || null;
  }

  /* ---- user tier, surfaced server-side (visitor | free | premium) ----- */
  function currentTier() {
    var host = document.querySelector('[data-lh-tier]');
    var t = host && host.getAttribute('data-lh-tier');
    return (t === 'free' || t === 'premium' || t === 'visitor') ? t : 'visitor';
  }

  /* ---- snake_case slug (batch-math -> batch_math, "Pricing breakdown" -> pricing_breakdown) */
  function slug(s) {
    return String(s || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60);
  }

  var tool = currentTool();

  /* =====================================================================
     1. learn_hub_tool_used — first genuine interaction, once per page view
     ---------------------------------------------------------------------
     The tools recompute live on every input (no "Calculate" button), and
     several render an initial result on load. So we do NOT count page load
     or the initial render — we count the first time the visitor actually
     touches a control inside the tool (not the nav, Academy band, locked
     panel, or waitlist form).
     ===================================================================== */
  function wireToolUsed() {
    if (!tool) return; // not on a known tool page (e.g. the hub landing)
    var fired = false;
    var IGNORE = '.learn-subnav, .learn-cta, .lh-locked, #learn-waitlist-form';

    function onFirst(e) {
      if (fired) return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('select, input, textarea, button, [role="button"]')) return;
      if (t.closest(IGNORE)) return;
      fired = true;
      detach();
      lhTrack('learn_hub_tool_used', { tool_name: tool, user_tier: currentTier() });
    }
    function detach() {
      document.removeEventListener('change', onFirst, true);
      document.removeEventListener('input', onFirst, true);
      document.removeEventListener('pointerdown', onFirst, true);
    }
    document.addEventListener('change', onFirst, true);
    document.addEventListener('input', onFirst, true);
    document.addEventListener('pointerdown', onFirst, true);
  }

  /* =====================================================================
     2. learn_hub_lock_viewed — locked panel actually scrolled into view
     ---------------------------------------------------------------------
     Fires once per panel per page view. A panel below the fold that's never
     scrolled to does NOT count (the "seen, not rendered" requirement).
     ===================================================================== */
  function wireLockViewed() {
    var panels = document.querySelectorAll('.lh-locked[data-tool]');
    if (!panels.length) return;

    function panelParams(panel) {
      var labelEl = panel.querySelector('.lh-locked__label');
      var feature = panel.getAttribute('data-lh-feature') ||
                    slug(labelEl ? labelEl.textContent : 'locked');
      return {
        tool_name: slug(panel.getAttribute('data-tool')) || (tool || 'unknown'),
        locked_feature: feature,
        user_tier: currentTier()
      };
    }
    function fire(panel) {
      if (panel.getAttribute('data-lh-seen')) return;
      panel.setAttribute('data-lh-seen', '1');
      lhTrack('learn_hub_lock_viewed', panelParams(panel));
    }

    if (!('IntersectionObserver' in window)) {
      // very old browser: best-effort, still once each
      Array.prototype.forEach.call(panels, fire);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // half the panel visible, OR (tall panels on mobile) it fills a good
        // chunk of the viewport — either way the visitor is looking at it
        var seen = entry.isIntersecting &&
          (entry.intersectionRatio >= 0.5 ||
           entry.intersectionRect.height >= window.innerHeight * 0.4);
        if (seen) {
          fire(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: [0, 0.25, 0.5] });

    Array.prototype.forEach.call(panels, function (p) { io.observe(p); });
  }

  /* =====================================================================
     3. learn_hub_cta_clicked — CTA inside a locked panel, before navigation
     ---------------------------------------------------------------------
     gtag sends via sendBeacon, so the event survives the page change.
     Delegated + capture phase so it runs before the link navigates.
     ===================================================================== */
  function wireCtaClicked() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('.lh-locked a[data-cta]');
      if (!link) return;
      var panel = link.closest('.lh-locked');
      lhTrack('learn_hub_cta_clicked', {
        tool_name: (panel && slug(panel.getAttribute('data-tool'))) || (tool || 'unknown'),
        cta_type: link.getAttribute('data-cta'),
        user_tier: currentTier()
      });
    }, true);
  }

  /* ---- go -------------------------------------------------------------- */
  function init() {
    wireToolUsed();
    wireLockViewed();
    wireCtaClicked();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();